import { deepClone } from '../core/clone';
import type { RoleId, RoleSetup } from '../types';

export interface RolePreset {
  playerCount: number;
  label: string;
  note: string;
  setup: RoleSetup;
}

const rep = (role: RoleId, n: number): RoleId[] => Array.from({ length: n }, () => role);

/**
 * Empfohlene, balancierte Kompositionen. Presets sind Empfehlungen, keine Spielregel –
 * der Spielleiter darf sie vollständig verändern.
 */
export const ROLE_PRESETS: Record<number, RolePreset> = {
  6: {
    playerCount: 6,
    label: 'Kleine Runde',
    note: 'Ein Vampir gegen ein kompaktes Dorf. Der Narr sorgt am ersten Tag für Unsicherheit.',
    setup: {
      roles: ['VAMPIRE', 'SEER', 'PRIEST', 'FOOL', ...rep('VILLAGER', 2)],
      drunk: { enabled: false, coveredRole: 'RANDOM' },
    },
  },
  7: {
    playerCount: 7,
    label: 'Stille Gassen',
    note: 'Ein Vampir, dafür starke Nachtrollen. Der Verfluchte stört die Seherin.',
    setup: {
      roles: ['VAMPIRE', 'SEER', 'WITCH', 'PRIEST', 'CURSED', ...rep('VILLAGER', 2)],
      drunk: { enabled: false, coveredRole: 'RANDOM' },
    },
  },
  8: {
    playerCount: 8,
    label: 'Zwei Schatten',
    note: 'Zwei Vampire gegen Seherin, Hexe und Priester.',
    setup: {
      roles: [...rep('VAMPIRE', 2), 'SEER', 'WITCH', 'PRIEST', 'FOOL', ...rep('VILLAGER', 2)],
      drunk: { enabled: false, coveredRole: 'RANDOM' },
    },
  },
  9: {
    playerCount: 9,
    label: 'Nebel über dem Markt',
    note: 'Erste Runde mit Trunkenbold und Vampirjägerin.',
    setup: {
      roles: [
        ...rep('VAMPIRE', 2),
        'SEER',
        'WITCH',
        'PRIEST',
        'VAMPIRE_HUNTRESS',
        'CURSED',
        ...rep('VILLAGER', 2),
      ],
      drunk: { enabled: true, coveredRole: 'RANDOM' },
    },
  },
  10: {
    playerCount: 10,
    label: 'Das volle Ensemble',
    note: 'Alle Sonderrollen im Spiel, der Trunkenbold verdeckt eine davon.',
    setup: {
      roles: [
        ...rep('VAMPIRE', 2),
        'SEER',
        'WITCH',
        'PRIEST',
        'VAMPIRE_HUNTRESS',
        'CURSED',
        'FOOL',
        ...rep('VILLAGER', 2),
      ],
      drunk: { enabled: true, coveredRole: 'RANDOM' },
    },
  },
  11: {
    playerCount: 11,
    label: 'Glockenschlag',
    note: 'Volles Ensemble mit einem zusätzlichen Dorfbewohner.',
    setup: {
      roles: [
        ...rep('VAMPIRE', 2),
        'SEER',
        'WITCH',
        'PRIEST',
        'VAMPIRE_HUNTRESS',
        'CURSED',
        'FOOL',
        ...rep('VILLAGER', 3),
      ],
      drunk: { enabled: true, coveredRole: 'RANDOM' },
    },
  },
  12: {
    playerCount: 12,
    label: 'Ganz Noxbury',
    note: 'Maximalbesetzung. Da höchstens zwei Vampire erlaubt sind, tragen alle Sonderrollen das Dorf.',
    setup: {
      roles: [
        ...rep('VAMPIRE', 2),
        'SEER',
        'WITCH',
        'PRIEST',
        'VAMPIRE_HUNTRESS',
        'CURSED',
        'FOOL',
        ...rep('VILLAGER', 4),
      ],
      drunk: { enabled: true, coveredRole: 'RANDOM' },
    },
  },
};

export function presetFor(playerCount: number): RoleSetup | null {
  const preset = ROLE_PRESETS[playerCount];
  return preset ? deepClone(preset.setup) : null;
}
